import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Ear, Sparkles, X, Plus } from "lucide-react";
import { useGlossary, biasForKey } from "@/lib/coachVoice";
import BiasNote from "@/components/BiasNote";

const HEARD = "/coach/heard";
const LEVELS = [["more", "More guidance"], ["balanced", "Balanced"], ["minimal", "Minimal"]];

// PATCH 32 — "Feels Heard" panel: surfaces H2, H1/H14, H6, H7, H8, H12, H13, H15 in one place.
export default function CoachHeardPanel({ projectId, sessionId = "panel-session" }) {
  const [sens, setSens] = useState("balanced");
  const [prefs, setPrefs] = useState([]);
  const [newPref, setNewPref] = useState("");
  const [chip, setChip] = useState(null);
  const [narr, setNarr] = useState("");
  const [mom, setMom] = useState(null);
  const [closeout, setCloseout] = useState(null);
  const [checkin, setCheckin] = useState(null);
  const [echoText, setEchoText] = useState("");
  const [echo, setEcho] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [study, setStudy] = useState(null);
  const glossary = useGlossary();

  // PATCH 40 Part 3 — map a fired intervention (from the ledger) to its plain-language bias, shown inline.
  const rowBias = (r) => {
    const t = (r.type || "").toLowerCase();
    if (t.includes("scope")) return biasForKey(glossary, "scope_boundary_erosion");
    if (t.includes("sunk")) return biasForKey(glossary, "sunk_cost_asymmetry");
    if (t.includes("velocity") || t.includes("prompt")) return biasForKey(glossary, "prompt_velocity");
    return null;
  };

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const [s, p, c, n, m] = await Promise.all([
        api.get(`${HEARD}/sensitivity/${projectId}`),
        api.get(`${HEARD}/preferences/${projectId}`),
        api.get(`${HEARD}/context-chip/${projectId}`),
        api.get(`${HEARD}/narrative/${projectId}`),
        api.get(`${HEARD}/momentum/${projectId}`),
      ]);
      setSens(s.data.sensitivity); setPrefs(p.data.preferences || []);
      setChip(c.data.has_context ? c.data.chip : null); setNarr(n.data.narrative); setMom(m.data);
    } catch (e) { /* project may be new */ }
    api.get(`${HEARD}/listening-checkin`).then(r => setCheckin(r.data.due ? r.data : null)).catch(() => {});
    api.get(`/projects/${projectId}/nudge-ledger`).then(r => setLedger(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/coach-study`).then(r => setStudy(r.data)).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const setLevel = async (lvl) => {
    setSens(lvl);
    try { await api.put(`${HEARD}/sensitivity/${projectId}`, { level: lvl }); toast.success(`Coaching set to ${lvl}`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const addPref = async () => {
    if (!newPref.trim()) return;
    try { const { data } = await api.post(`${HEARD}/preferences`, { project_id: projectId, text: newPref }); setPrefs([data.preference, ...prefs]); setNewPref(""); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delPref = async (id) => {
    try { await api.delete(`${HEARD}/preferences/${id}`, { params: { project_id: projectId } }); setPrefs(prefs.filter(p => p.id !== id)); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const runEcho = async () => {
    try { const { data } = await api.post(`${HEARD}/echo`, { prompt: echoText, project_id: projectId }); setEcho(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const confirmEcho = async (understood, mode, corrected) => {
    try { await api.post(`${HEARD}/echo/confirm`, { project_id: projectId, understood_as: understood, mode, corrected }); toast.success("Understanding confirmed"); setEcho(null); setEchoText(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const showCloseout = async () => {
    try { const { data } = await api.get(`${HEARD}/session-closeout/${projectId}`); setCloseout(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const answerCheckin = async (answer) => {
    try { await api.post(`${HEARD}/listening-checkin`, { answer }); setCheckin(null); toast.success("Thanks — that shapes the Coach's tone."); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!projectId) return null;
  return (
    <div className="k-container" data-testid="coach-heard-panel" style={{ padding: 18, marginBottom: 14, borderLeft: "3px solid var(--accent-primary)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Ear size={18} color="var(--accent-primary)" />
        <h3 className="k-heading" style={{ fontSize: 16, margin: 0 }}>Feels heard — coach responsiveness</h3>
      </div>

      {checkin && (
        <div className="k-control" data-testid="listening-checkin" style={{ marginBottom: 12 }}>
          <div className="k-heading" style={{ fontSize: 13 }}>{checkin.question}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            {checkin.options.map(o => <button key={o} className="k-btn k-btn-ghost" data-testid={`checkin-${o}`} style={{ padding: "6px 12px", fontSize: 12 }} onClick={() => answerCheckin(o)}>{o}</button>)}
            <button className="k-link" data-testid="checkin-dismiss" style={{ fontSize: 12 }} onClick={() => setCheckin(null)}>Not now</button>
          </div>
        </div>
      )}

      {chip && (
        <div className="k-tag-info" data-testid="context-chip" style={{ fontSize: 12, marginBottom: 12, padding: 8, borderRadius: 8 }}>{chip}</div>
      )}

      {/* H2 sensitivity dial */}
      <div style={{ marginBottom: 14 }}>
        <div className="k-muted" style={{ fontSize: 12, marginBottom: 6 }}>Coaching sensitivity (governs all 25 features at once)</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {LEVELS.map(([k, label]) => (
            <button key={k} data-testid={`sensitivity-${k}`} className={sens === k ? "k-btn k-btn-primary" : "k-btn k-btn-ghost"} style={{ padding: "6px 14px", fontSize: 13 }} onClick={() => setLevel(k)}>{label}</button>
          ))}
        </div>
      </div>

      {/* H6 narrative */}
      {narr && <p className="k-muted" data-testid="project-narrative" style={{ fontSize: 12.5, lineHeight: 1.6, background: "var(--control)", padding: 10, borderRadius: 8 }}>{narr}</p>}

      {/* H12 momentum */}
      {mom?.encouragement && <div className="k-tag-green" data-testid="momentum" style={{ fontSize: 12, padding: 8, borderRadius: 8, marginBottom: 12, display: "inline-flex", alignItems: "center", gap: 6 }}><Sparkles size={13} /> {mom.encouragement}</div>}

      {/* H8 preferences */}
      <div style={{ marginBottom: 14 }}>
        <div className="k-muted" style={{ fontSize: 12, marginBottom: 6 }}>Teach the Coach a standing preference</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input className="k-control" data-testid="pref-input" placeholder="e.g. always show me the simplest option first" value={newPref} onChange={e => setNewPref(e.target.value)} />
          <button className="k-btn k-btn-primary" data-testid="pref-add" onClick={addPref}><Plus size={14} /> Teach</button>
        </div>
        {prefs.map(p => (
          <div key={p.id} data-testid={`pref-${p.id}`} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
            <span className="k-muted" style={{ fontSize: 12, flex: 1 }}>“{p.text}”</span>
            <button className="k-btn k-btn-ghost" data-testid={`pref-del-${p.id}`} style={{ padding: "4px 8px" }} onClick={() => delPref(p.id)}><X size={12} /></button>
          </div>
        ))}
      </div>

      {/* H1/H14 echo tester */}
      <div style={{ marginBottom: 12 }}>
        <div className="k-muted" style={{ fontSize: 12, marginBottom: 6 }}>Confirmed-understanding echo</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input className="k-control" data-testid="echo-input" placeholder="Describe a change; I'll reflect it back first" value={echoText} onChange={e => setEchoText(e.target.value)} />
          <button className="k-btn k-btn-ghost" data-testid="echo-run" onClick={runEcho}>Reflect back</button>
        </div>
        {echo && (
          <div className="k-control" data-testid="echo-result" style={{ marginTop: 8 }}>
            <div className="k-heading" style={{ fontSize: 13 }}>I understood this as:</div>
            <input className="k-control" data-testid="echo-understood" defaultValue={echo.understood_as} id="echo-edit" style={{ marginTop: 6 }} />
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              <button className="k-btn k-btn-primary" data-testid="echo-confirm" style={{ padding: "6px 12px", fontSize: 12 }} onClick={() => confirmEcho(echo.understood_as, "confirm", false)}>That's right</button>
              <button className="k-btn k-btn-ghost" data-testid="echo-edit-confirm" style={{ padding: "6px 12px", fontSize: 12 }} onClick={() => confirmEcho(document.getElementById("echo-edit").value, "edit_text", true)}>Use my edit</button>
            </div>
            <div className="k-muted" style={{ fontSize: 11, marginTop: 8 }}>Or pick a closer option:</div>
            {echo.alternatives.map((a, i) => (
              <button key={i} className="k-link" data-testid={`echo-alt-${i}`} style={{ display: "block", fontSize: 12, textAlign: "left", marginTop: 4 }} onClick={() => confirmEcho(a, "pick_alternative", true)}>{a}</button>
            ))}
          </div>
        )}
      </div>

      {/* P3 nudge ledger + P9 study (patent-grade transparency) */}
      <div style={{ marginBottom: 12 }}>
        <div className="k-muted" style={{ fontSize: 12, marginBottom: 6 }}>Nudge Transparency Ledger — every intervention that fired for this project</div>
        {ledger?.ledger?.length ? ledger.ledger.slice(0, 6).map((r, i) => (
          <div key={i} data-testid={`ledger-row-${i}`} style={{ padding: "3px 0", borderBottom: "1px solid var(--border)" }}>
            <div className="k-muted" style={{ fontSize: 11.5, display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span>{r.type}</span><span style={{ flex: 1 }}>{r.detail}</span><span className="k-tag-info" style={{ fontSize: 10 }}>{r.action}</span>
            </div>
            <BiasNote bias={rowBias(r)} testid={`ledger-bias-${i}`} />
          </div>
        )) : <p className="k-muted" style={{ fontSize: 12 }} data-testid="ledger-empty">No interventions logged yet.</p>}
        {ledger?.active_trigger_tuning?.length > 0 && (
          <div className="k-tag-warn" data-testid="trigger-tuning" style={{ fontSize: 11, marginTop: 6, padding: 6, borderRadius: 6 }}>
            Disclosed trigger tuning is active. <button className="k-link" data-testid="experiment-opt-out" style={{ fontSize: 11 }} onClick={async () => { try { await api.post(`/projects/${projectId}/experiment-opt-out`, null, { params: { opt_out: true } }); toast.success("Opted out — nothing about your build changes"); load(); } catch (e) { toast.error(errMsg(e)); } }}>Opt out</button>
          </div>
        )}
        {study && (
          <label className="k-muted" data-testid="coach-study" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
            <input type="checkbox" data-testid="study-optin" checked={!!study.enrolled}
              onChange={async e => { try { const { data } = await api.post(`/projects/${projectId}/coach-study`, null, { params: { enrol: e.target.checked } }); setStudy({ ...study, enrolled: data.enrolled }); toast.success(data.enrolled ? "Enrolled (opt-in)" : "Left the study"); } catch (err) { toast.error(errMsg(err)); } }} />
            Opt in to the disclosed Coach vs no-Coach study — never affects your build, price or support.
          </label>
        )}
      </div>

      {/* H13 close-out */}
      <button className="k-btn k-btn-ghost" data-testid="closeout-btn" style={{ fontSize: 12, padding: "6px 12px" }} onClick={showCloseout}>Session reflection</button>
      {closeout && (
        <div className="k-control" data-testid="closeout" style={{ marginTop: 8 }}>
          <div className="k-muted" style={{ fontSize: 12 }}>Goal: “{closeout.goal}”</div>
          <div className="k-heading" style={{ fontSize: 13, marginTop: 4 }}>{closeout.done_count} things done · {closeout.open_count} open</div>
          <div className="k-muted" style={{ fontSize: 12, marginTop: 4 }}>Before next time: {closeout.one_thing}</div>
          {closeout.bias_reflection && (
            <div className="k-muted" data-testid="closeout-bias" style={{ fontSize: 12, marginTop: 8, paddingTop: 8, borderTop: "1px dashed var(--border-strong)", lineHeight: 1.55 }}>
              {closeout.bias_reflection.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
